/**
 * Draft payloads aligned with news-portal-backend domain types (CMS ingest JSON).
 * Stored in flock notebook draft.content with a version prefix.
 *
 * @see news-portal-backend/internal/domain/recipe/recipe.go
 * @see news-portal-backend/internal/domain/movie/movie.go
 * @see news-portal-backend/internal/domain/news/event.go
 * @see news-portal-backend/internal/domain/news/article.go
 */

import type { DraftNotebookKind } from '../utils/draftNotebookKind';
import { notebookHadDraftLifetimeSave } from '../utils/notebookDraftLifetimeSave';
import {
  parseRecipeMarkdown,
  type RecipeDraftFields,
} from '../utils/structuredDraftMarkdown';

export const PORTAL_DRAFT_MAGIC = 'portal-draft:v1\n';

export type PortalContentBlock = {
  type: string;
  text?: string;
  html?: string;
  src?: string;
  description?: string;
  urls?: string[];
  embed_url?: string;
  provider?: string;
};

/** recipe.Recipe — JSON field names match Go struct tags. */
export type PortalRecipePayload = {
  id: string;
  title: string;
  description: string;
  image: string;
  course: string;
  difficulty: string;
  ingredients: string[];
  steps: string[];
  prepMinutes: number;
  servings: number;
  segments?: Record<string, unknown>;
};

/** movie.Movie */
export type PortalMoviePayload = {
  id: string;
  movie_title: string;
  saint_names?: string[];
  saint_reference_urls?: string[];
  youtube_url: string;
  notes: string;
  /** Editor.js blocks for notes. `notes` stays the plain text. */
  notes_blocks?: PortalContentBlock[];
  duration_minutes: number;
  thumbnail_url: string;
  availability: string;
  rental_price_eur?: number | null;
  rating: number;
  rating_source?: string;
  /** Editor: direct link to IMDb/RT review or search page (optional). */
  rating_review_url?: string;
  /** Per-source review links (preferred over `rating_review_url` when set). */
  rating_review_url_imdb?: string;
  rating_review_url_rotten_tomatoes?: string;
  /** 1–5 stars for IMDb tab (editor interpretation of that source). */
  rating_imdb?: number;
  /** 1–5 stars for Rotten Tomatoes tab. */
  rating_rotten_tomatoes?: number;
  rating_details?: unknown;
  age_rating?: string;
  labels?: string[];
  plot_summary: string;
  /** Editor.js blocks for the plot. `plot_summary` stays the plain text the portal stores. */
  plot_blocks?: PortalContentBlock[];
  discussion_questions: string[];
  /** Editor.js blocks for the questions. `discussion_questions` stays one plain string per question. */
  discussion_blocks?: PortalContentBlock[];
};

/** news.Event — times as ISO-8601 strings for JSON round-trip. */
export type PortalEventPayload = {
  id: string;
  title: string;
  url: string;
  datum: string;
  ura: string;
  trajanje: string;
  location?: string;
  place_id?: string;
  datum_parsed?: string;
  trajanje_start?: string;
  trajanje_end?: string;
  dedup_key: string;
  og_image?: string;
  content_blocks: PortalContentBlock[];
  scraped_at: string;
  updated_at: string;
  check_in_enabled?: boolean;
  /** Flock / editor: whole-day event (times stored as local start/end of day). */
  all_day?: boolean;
};

/** news.Article */
export type PortalArticlePayload = {
  id: string;
  url?: string;
  title: string;
  excerpt: string;
  content?: string;
  category: string;
  author_id?: string;
  author?: string;
  publication_datetime: string;
  updated: string;
  read_time: string;
  og_image?: string;
  entities: unknown[];
  content_blocks: PortalContentBlock[];
  tags: string[];
};

export type PortalDraftEnvelope =
  | { schemaVersion: 1; kind: 'recipe'; payload: PortalRecipePayload }
  | { schemaVersion: 1; kind: 'movie'; payload: PortalMoviePayload }
  | { schemaVersion: 1; kind: 'event'; payload: PortalEventPayload }
  | { schemaVersion: 1; kind: 'article'; payload: PortalArticlePayload };

function nowIso(): string {
  return new Date().toISOString();
}

export function defaultRecipePayload(notebookId: string): PortalRecipePayload {
  return {
    id: notebookId,
    title: 'Untitled recipe',
    description: '',
    image: '',
    course: '',
    difficulty: '',
    ingredients: [],
    steps: [],
    prepMinutes: 0,
    servings: 0,
  };
}

export function defaultMoviePayload(notebookId: string): PortalMoviePayload {
  return {
    id: notebookId,
    movie_title: 'Untitled movie',
    youtube_url: '',
    notes: '',
    duration_minutes: 0,
    thumbnail_url: '',
    availability: 'not_available',
    rating: 0,
    plot_summary: '',
    discussion_questions: [],
    labels: [],
  };
}

export function defaultEventPayload(notebookId: string): PortalEventPayload {
  const t = nowIso();
  const start = new Date();
  start.setSeconds(0, 0);
  start.setMinutes(0);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  const datumParsed = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
  const uraStr = `${pad(start.getHours())}:${pad(start.getMinutes())}`;
  return {
    id: notebookId,
    title: 'Untitled event',
    url: '',
    datum: start.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
    ura: uraStr,
    trajanje: '1 hr',
    location: '',
    dedup_key: `draft-${notebookId}`,
    datum_parsed: datumParsed,
    trajanje_start: start.toISOString(),
    trajanje_end: end.toISOString(),
    content_blocks: [],
    scraped_at: t,
    updated_at: t,
    check_in_enabled: false,
    all_day: false,
  };
}

export function defaultArticlePayload(notebookId: string): PortalArticlePayload {
  const t = nowIso();
  return {
    id: notebookId,
    title: 'Untitled article',
    excerpt: '',
    content: '',
    category: 'News',
    publication_datetime: t,
    updated: t,
    read_time: '1 min',
    content_blocks: [],
    tags: [],
    entities: [],
  };
}

export function defaultEnvelopeForKind(
  notebookId: string,
  kind: DraftNotebookKind
): PortalDraftEnvelope {
  switch (kind) {
    case 'recipe':
      return { schemaVersion: 1, kind: 'recipe', payload: defaultRecipePayload(notebookId) };
    case 'movie':
      return { schemaVersion: 1, kind: 'movie', payload: defaultMoviePayload(notebookId) };
    case 'event':
      return { schemaVersion: 1, kind: 'event', payload: defaultEventPayload(notebookId) };
    case 'article':
    default:
      return { schemaVersion: 1, kind: 'article', payload: defaultArticlePayload(notebookId) };
  }
}

export function serializePortalDraft(envelope: PortalDraftEnvelope): string {
  return PORTAL_DRAFT_MAGIC + JSON.stringify(envelope);
}

export function tryParsePortalDraft(raw: string): PortalDraftEnvelope | null {
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith(PORTAL_DRAFT_MAGIC)) return null;
  try {
    const parsed = JSON.parse(raw.slice(PORTAL_DRAFT_MAGIC.length)) as PortalDraftEnvelope;
    if (parsed?.schemaVersion !== 1 || !parsed.kind || !(parsed as any).payload) return null;
    return parsed;
  } catch {
    return null;
  }
}

function recipeFromLegacyMarkdown(notebookId: string, title: string, md: string): PortalRecipePayload {
  const f: RecipeDraftFields = parseRecipeMarkdown(md);
  const ingredients = f.ingredients
    .split(/\n/)
    .map((s) => s.replace(/^\s*[-*]\s*/, '').trim())
    .filter(Boolean);
  const steps = f.instructions
    .split(/\n/)
    .map((s) => s.replace(/^\s*\d+\.\s*/, '').trim())
    .filter(Boolean);
  let prepMinutes = 0;
  let servings = 0;
  const st = f.servingsTiming.toLowerCase();
  const prepM = st.match(/prep\s*(\d+)\s*min/);
  if (prepM) prepMinutes = parseInt(prepM[1], 10);
  const servM = st.match(/serves?\s*(\d+)/i);
  if (servM) servings = parseInt(servM[1], 10);
  return {
    id: notebookId,
    title: title || 'Untitled recipe',
    description: f.notes || '',
    image: '',
    course: '',
    difficulty: '',
    ingredients,
    steps,
    prepMinutes,
    servings,
  };
}

/** Normalize draft file to a v1 envelope for this notebook + kind. */
export function ensurePortalEnvelope(
  draftContent: string,
  draftTitle: string,
  notebookId: string,
  kind: DraftNotebookKind
): PortalDraftEnvelope {
  const existing = tryParsePortalDraft(draftContent);
  if (existing && existing.kind === kind) return existing;

  if (kind === 'recipe' && draftContent && !existing) {
    return {
      schemaVersion: 1,
      kind: 'recipe',
      payload: recipeFromLegacyMarkdown(notebookId, draftTitle, draftContent),
    };
  }

  if (kind === 'article' && draftContent && !existing) {
    const base = defaultArticlePayload(notebookId);
    base.title = draftTitle || base.title;
    base.content = draftContent;
    if (!base.content_blocks.length && draftContent.trim()) {
      base.content_blocks = [{ type: 'paragraph', text: draftContent }];
    }
    return { schemaVersion: 1, kind: 'article', payload: base };
  }

  return defaultEnvelopeForKind(notebookId, kind);
}

/** Whether draft.content has enough substance for actions like AI generate (skips empty defaults). */
export function portalDraftHasSubstantiveBody(raw: string): boolean {
  const trimmed = raw.trim();
  if (!trimmed) return false;
  const env = tryParsePortalDraft(trimmed);
  if (!env) return true;
  switch (env.kind) {
    case 'movie': {
      const m = env.payload;
      return Boolean(
        m.plot_summary?.trim() ||
          m.notes?.trim() ||
          m.youtube_url?.trim() ||
          m.thumbnail_url?.trim() ||
          (m.duration_minutes ?? 0) > 0 ||
          (m.rating ?? 0) > 0 ||
          (m.labels?.length ?? 0) > 0 ||
          (m.saint_names?.length ?? 0) > 0 ||
          Boolean(m.discussion_questions?.some((q) => (q || '').trim())) ||
          (m.movie_title?.trim() && m.movie_title !== 'Untitled movie') ||
          (m.availability && m.availability !== 'not_available')
      );
    }
    case 'recipe': {
      const r = env.payload;
      return Boolean(
        r.ingredients.some((x) => x?.trim()) ||
          r.steps.some((x) => x?.trim()) ||
          r.description?.trim() ||
          r.course?.trim() ||
          r.difficulty?.trim() ||
          (r.prepMinutes ?? 0) > 0 ||
          (r.servings ?? 0) > 0 ||
          (r.image || '').trim() ||
          (r.title?.trim() && r.title !== 'Untitled recipe')
      );
    }
    case 'event': {
      const ev = env.payload;
      return Boolean(
        (ev.title?.trim() && ev.title !== 'Untitled event') ||
          ev.location?.trim() ||
          (ev.url || '').trim() ||
          ev.content_blocks?.some((b) => (b.text || b.html || '').trim()) ||
          ev.check_in_enabled === true ||
          ev.all_day === true
      );
    }
    case 'article': {
      const a = env.payload;
      return Boolean(
        a.excerpt?.trim() ||
          a.content?.trim() ||
          a.content_blocks?.some((b) => (b.text || b.html || '').trim()) ||
          (a.tags?.length ?? 0) > 0 ||
          (a.title?.trim() && a.title !== 'Untitled article')
      );
    }
  }
}

/**
 * Portal-only library drafts with no substantive edits and no attached clusters or
 * generated extras are hidden from the list and deleted when the drawer closes,
 * unless the notebook has had at least one lifetime "save" (see `notebookDraftLifetimeSave`).
 */
export function isDiscardableEmptyLibraryDraft(notebook: {
  id?: string;
  draft?: {
    content?: string;
    ready_for_publishing?: boolean;
    contrasting_views?: unknown[] | null;
    social_media_posts?: unknown[] | null;
    suggested_titles?: unknown[] | null;
    images?: unknown[] | null;
  } | null;
  cluster_ids?: string[];
  clusters?: unknown[] | null;
}): boolean {
  if (notebook.id?.startsWith('demo-notebook')) return false;
  if (notebook.id && notebookHadDraftLifetimeSave(notebook.id)) return false;

  const d = notebook.draft;
  if (!d || d.ready_for_publishing) return false;
  if (d.contrasting_views && d.contrasting_views.length > 0) return false;
  if (d.social_media_posts && d.social_media_posts.length > 0) return false;
  if (d.suggested_titles && d.suggested_titles.length > 0) return false;
  if (d.images && d.images.length > 0) return false;

  const raw = (d.content || '').trim();
  if (!raw.startsWith(PORTAL_DRAFT_MAGIC)) return false;
  if (!tryParsePortalDraft(raw)) return false;

  const hasClusters =
    (notebook.cluster_ids?.length ?? 0) > 0 || (Array.isArray(notebook.clusters) && notebook.clusters.length > 0);
  if (hasClusters) return false;

  return !portalDraftHasSubstantiveBody(raw);
}

export function primaryTitleFromEnvelope(e: PortalDraftEnvelope): string {
  switch (e.kind) {
    case 'recipe':
      return e.payload.title;
    case 'movie':
      return e.payload.movie_title;
    case 'event':
    case 'article':
      return e.payload.title;
  }
}

/** True when the portal primary title is still the empty template (Library should fall back to notebook name). */
export function isDefaultPortalPrimaryTitle(kind: PortalDraftEnvelope['kind'], title: string): boolean {
  const t = (title || '').trim();
  if (!t) return true;
  switch (kind) {
    case 'article':
      return t === 'Untitled article';
    case 'recipe':
      return t === 'Untitled recipe';
    case 'movie':
      return t === 'Untitled movie';
    case 'event':
      return t === 'Untitled event';
    default:
      return false;
  }
}

export function syncEnvelopePrimaryTitle(e: PortalDraftEnvelope, title: string): PortalDraftEnvelope {
  switch (e.kind) {
    case 'recipe':
      return { ...e, payload: { ...e.payload, title } };
    case 'movie':
      return { ...e, payload: { ...e.payload, movie_title: title } };
    case 'event':
      return { ...e, payload: { ...e.payload, title } };
    case 'article':
      return { ...e, payload: { ...e.payload, title } };
  }
}

/** If the envelope still has a template title, replace it with the notebook display name. */
export function alignEnvelopeWithNotebookNameIfPlaceholder(
  env: PortalDraftEnvelope,
  notebookName: string
): PortalDraftEnvelope {
  const hint = (notebookName || '').trim();
  if (!hint) return env;
  const cur = primaryTitleFromEnvelope(env).trim();
  if (!isDefaultPortalPrimaryTitle(env.kind, cur)) return env;
  return syncEnvelopePrimaryTitle(env, hint);
}

/** Update primary display title inside portal-draft JSON (Library drawer header). */
export function patchOrEnsurePortalPrimaryTitle(
  draftContent: string,
  title: string,
  notebookId: string,
  kind: DraftNotebookKind
): string {
  const existing = tryParsePortalDraft(draftContent.trim());
  if (existing && existing.kind === kind) {
    return serializePortalDraft(syncEnvelopePrimaryTitle(existing, title));
  }
  const env = ensurePortalEnvelope(draftContent, title, notebookId, kind);
  return serializePortalDraft(syncEnvelopePrimaryTitle(env, title));
}
